import AppTitle from '../support/AppTitle.js';
import useAuth from "../auth/useAuth.js";
import useSettings from "../settings/useSettings.js";
import Hijack from '../support/Hijack.js';
import PageService from '../pages/PageService.js';
import PagePreview from '../pages/PagePreview.js';
import GlossaryService from '../glossary/GlossaryService.js';
import NotFound from '../support/NotFound.js';
import Areas from '../support/Areas.js';
import LoginSSO from '../auth/LoginSSO.js';
import TranscriptButton from '../support/TranscriptButton.js';

import React, { useState, useEffect, useRef, lazy, Suspense, useMemo } from 'react';
import { Link as RouterLink, useLocation, useHistory } from "react-router-dom";
import { makeStyles } from '@material-ui/core/styles';
import { Helmet } from "react-helmet";
import Moment from 'react-moment';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Typography from '@material-ui/core/Typography';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import Tooltip from '@material-ui/core/Tooltip';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Edit from '@material-ui/icons/Edit';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import Box from '@material-ui/core/Box';
import RestorePageIcon from '@material-ui/icons/RestorePage';
import DeleteIcon from '@material-ui/icons/Delete';
import Alert from '@material-ui/lab/Alert';
import CircularProgress from '@material-ui/core/CircularProgress';
import PrintIcon from '@material-ui/icons/Print';
import IconButton from '@material-ui/core/IconButton';
import Grid from '@material-ui/core/Grid';
import Collapse from '@material-ui/core/Collapse';

import FacebookIcon from '@material-ui/icons/Facebook';
import LinkedInIcon from '@material-ui/icons/LinkedIn';
import TwitterIcon from '@material-ui/icons/Twitter';

import { EmailShareButton, FacebookShareButton, LinkedinShareButton, TwitterShareButton} from "react-share";

const VersionPicker = lazy(() => import('../pages/VersionPicker.js'));

const pageStyles = makeStyles((theme) => ({
    page: {
        overflowWrap: "break-word",
        "& img": {
            maxWidth: "100%",
            height: "auto"
        }
    },
    noprint: {
        "@media print": {
            display: "none !important"
        }
    },
    description: {
        marginBottom: theme.spacing(2)
    },
    category: {
        paddingBottom: theme.spacing(2),
        marginBottom: theme.spacing(2),
        marginTop: theme.spacing(3)
    },
    collapse: {
        color: "var(--red) !important",
        padding: "0 !important",
        marginBottom: theme.spacing(2),
        fontSize: "0.9rem !important"
    },
    chipWarn: {
        background: 'rgb(237, 108, 2)'
    }
}));
const Page = (props) => {
    const auth = useAuth();
    const classes = pageStyles();
    const { id } = props;
    const [overrideHead, setOverrideHead] = useState(props?.overrideHead ?? true);
    const [byFamily, setByFamily] = useState(props?.byFamily ?? true);
    const [area, setArea] = useState(props?.area);
    const [data, setData] = useState(props?.data ?? postProcess(PageService.getCached(id)));
    const [embed, setEmbed] = useState(props?.embed ?? false);
    const [canContribute, setCanContribute] = useState(false);
    const [canApprove, setCanApprove] = useState(false);
    const [canObserve, setCanObserve] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [needsApproval, setNeedsApproval] = useState(false);
    const [approving, setApproving] = useState(false);
    const [restoring, setRestoring] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [version, setVersion] = useState(null);
    const [glossary, setGlossary] = useState(GlossaryService.cachedList);
    const [loading, setLoading] = useState(false);
    const [_403, set403] = useState(false);
    const settings = useSettings();
    const contentRef = useRef(null);
    const location = useLocation();
    const history = useHistory();

    function postProcess(d) {
        if (d && 'categories' in d && d.categories && typeof d.categories == "string") {
            d.categories = JSON.parse(d.categories);
        }
        return d;
    }

    const areaDef = useMemo(() => {
        return Areas.find(a => a.url === area);
    }, [area]);

    const [lastGlossaryTime, setLastGlossaryTime] = useState(0);
    useEffect(() => {
        var updated = settings.get("Glossary last updated");
        if (lastGlossaryTime != updated) {
            setLastGlossaryTime(updated);
            GlossaryService.getList({}, updated).then((response) => {
                setGlossary(response);
            });
        }
    }, [settings]);

    useEffect(() => {
        if (data) {
            if (data.area == "article" || data.area == "showcase" || data.area == "collection" || data.area == "page" || data.area == "unlisted" || data.area == "renaissance_showcase" || data.area == "renaissance_events" || data.area == "events") {
                setCanContribute(auth.user.id && auth.user?.roles && auth.user.roles.indexOf(data.area + ".contributor") !== -1);
                setCanApprove(auth.user.id && auth.user?.roles && auth.user.roles.indexOf(data.area + ".approver") !== -1);
            } else if (data.area != "search" && auth.user.id && auth.user?.roles && auth.user.roles.indexOf("admin") !== -1) {
                setCanContribute(true);
                setCanApprove(true);
            }
            setIsAdmin(auth.user.id && auth.user?.roles && auth.user.roles.indexOf("admin") !== -1);
            setCanObserve(auth.user.id && auth.user?.roles
                && (auth.user.roles.indexOf("observer") !== -1 || auth.user.roles.indexOf(data.area + ".observer") !== -1));
        }
    }, [auth, data]);

    useEffect(() => {
        //setData({});
        if (auth.loading)
            return;
        if (!data)
            setLoading(true);
        var query = new URLSearchParams(location.search);
        var req = { id: id, byFamily: byFamily ? 1 : 0, redirect: isAdmin ? 0 : 1 };
        var campaign = query.get('campaign');
        if (campaign)
            req.campaign = campaign;

        PageService.getPage(req)
            .then((response) => {
                set403(false);
                if ('data' in response && 'data' in response.data) {
                    response.data.data = postProcess(response.data.data);
                    setData(response.data.data);
                    setVersion(response.data.data);
                    setLoading(false);

                    if (response.data.data.state == 'workflow')
                        setNeedsApproval(true);
                    else if (!embed) {
                        var hasUrl = location.pathname.endsWith("/" + response.data.data.url),
                            hasId = location.pathname.endsWith("/" + response.data.data.id),
                            hasFamily = location.pathname.endsWith("/" + response.data.data.family);
                        var redirectPath = null;
                        if (!hasUrl) {
                            if (response.data.data.url)
                                redirectPath = response.data.data.url;
                            if (!hasId) {
                                if (response.data.data.id)
                                    redirectPath = response.data.data.id;
                                if (!hasFamily) {
                                    if (response.data.data.family)
                                        redirectPath = response.data.data.family;
                                }
                            }
                        }
                        if (redirectPath != null) {
                            history.replace("/" + response.data.data.area + "/" + redirectPath + location.search + location.hash);
                        }
                    }
                }
            })
            .catch(function (e) {
                if (e.response && e.response.status === 403) {
                    set403(true);
                    localStorage.removeItem("lthub_cached_page_" + encodeURIComponent(id));
                }
                setData(null);
                setLoading(false);
                return false;//dont propagate to top level
            });
    }, [id, auth]);


    const scrollToHash = () => {
        var scrollTo = document.getElementById(location.hash.replace("#", ""));
        if (scrollTo)
            setTimeout(function () { scrollTo.scrollIntoView({ behavior: "smooth" }); }, 500);
    };
    useEffect(() => {
        if (contentRef.current == null) return;
        scrollToHash();
    }, [data]);

    const handleApprove = () => { setApproving(true); };
    const handleApproveClose = () => { setApproving(false); };
    const handleApproveConfirm = () => {
        return PageService.approvePage({ id: data.id }).then((response) => {
            setApproving(false);
            var oldData = { ...data };
            oldData.state = response.data.state;
            setNeedsApproval(response.data.state == 'workflow');
            setData(oldData);
        }).catch((error) => {

        });
    };

    const handleRestore = () => { setRestoring(true); setVersion(data); };
    const handleRestoreClose = () => { setRestoring(false); };
    const handleRestoreConfirm = () => {
        return PageService.restorePage({ id: version.id }).then((response) => {
            setRestoring(false);
            setData(postProcess(response.data.data));
        }).catch((error) => {

        });
    };
    const handleRevertConfirm = () => {
        return PageService.revertPage({ id: version.id }).then((response) => {
            setRestoring(false);
            setData(postProcess(response.data.data));
        }).catch((error) => {

        });
    };

    const handleDelete = () => { setDeleting(true); };
    const handleDeleteClose = () => { setDeleting(false); };
    const handleDeleteConfirm = () => {
        return PageService.deletePage({ id: data.id }).then((response) => {
            setDeleting(false);
            setData(null);
        }).catch((error) => {

        });
    };

    const controlButtons = function () {
        var buttons = [];
        if (canContribute) {
            buttons.push(<Tooltip key="edit-btn" title="Make changes to the page"><Button variant="contained" component={RouterLink} to={"/" + area.url + "/" + data.id + "/edit"}>
                <Edit /> Edit {area.name}</Button></Tooltip>);
        }
        if (canApprove) {
            if (needsApproval) {
                buttons.push(<Tooltip key="approve-btn" title="Approve page in workflow"><Button onClick={handleApprove} ><ThumbUpIcon /> Approve</Button></Tooltip>);
            }
            buttons.push(<Tooltip key="restore-btn" title="View old versions and restore them to be current verison"><Button color="primary" onClick={handleRestore}><RestorePageIcon /> Versions</Button></Tooltip>);
            buttons.push(<Tooltip key="delete-btn" title="Delete page"><Button color="secondary" onClick={handleDelete}><DeleteIcon /> Delete</Button></Tooltip>);
        }
        if (buttons.length == 0 && canObserve && data?.state === "workflow") {
            buttons.push(<Chip key="observe-btn" label="DRAFT" />);
        }
        if (data?.redirect && isAdmin) {
            buttons.push(<Chip key="redirect-btn" label={<RouterLink to={"/page/" + data.redirect}>Redirects</RouterLink>} className={classes.chipWarn} />);
        }
        return buttons;
    };

    const itemsForCategory = (cat) => {
        var out = [];
        if (data.items)
            for (var i = 0; i < data.items.length; i++) {
                if (data.items[i].category == cat)
                    out.push(data.items[i]);
            }
        return out;
    };
    const renderContent = function () {
        if (!data) return;
        switch (data.area) {
            case "collection":
                return (<React.Fragment>
                    <Hijack data={data}><div ref={contentRef} dangerouslySetInnerHTML={{ __html: data?.content ?? "" }} className={classes.page}></div></Hijack>
                    {data.categories ? data.categories.map((cat, index) => {
                        return (
                            <React.Fragment key={cat.id}><hr /><Category category={cat} items={itemsForCategory(cat.id)} collection={data} /></React.Fragment>
                        );
                    }) : null}
                </React.Fragment>);
                break;
            case "renaissance_showcase":
                return (<React.Fragment>
                    <Hijack data={data} ><div ref={contentRef} dangerouslySetInnerHTML={{ __html: data?.video ?? "" }} className={classes.page}></div></Hijack>
                </React.Fragment>);
                break;
            default:
                return (<Hijack data={data}>
                    <div ref={contentRef} dangerouslySetInnerHTML={{ __html: data?.content ?? "" }} className={classes.page}></div>
                </Hijack>);
        }
        return null;
    }

    const print = () => {
        window.print();
    };

    const baseUrl = document.getElementsByTagName('base')[0].getAttribute('href');
    return (
        <div>
            {data && !embed && (area.features.indexOf("breadcrumbs") !== -1 || (location.state && location.state.collection)) && !data.hideBreadcrumbs ?          
            <Breadcrumbs aria-label="breadcrumb">
                <RouterLink to="/">Home</RouterLink>
                {(location.state && location.state.collection)
                    ?
                    <RouterLink to={"/collection/" + (location.state.collection.url || location.state.collection.id)}>{location.state.collection.title}</RouterLink>
                    :
                    (area.url != "collection" ?
                        (area.features.indexOf("simplebreadcrumbs") === -1 ? <RouterLink to={"/" + area.url}>{area.plural}</RouterLink> : null)
                        : <RouterLink to={"/article"}>Articles</RouterLink>)
                }
                {data ? (<Typography color="textPrimary">{data.title}</Typography>) : null}
            </Breadcrumbs> 
            : null}
            {data ? (
                <div>
                    {!embed ?
                        <div>
                            <Helmet>
                                <title>{AppTitle((data && data.title) ? data.title : (loading ? "Loading..." : "Page not found"))}</title>
                                <link rel="canonical" href={data.area + "/" + (data.url || data.id)} />
                                <meta property="og:url" content={data.area + "/" + (data.url || data.id)} />
                                <meta property="og:title" content={data.title} />
                                {area.url == "unlisted" ? (<meta name="robots" content="noindex" />) : null}
                            </Helmet>
                          
                                <Box display="flex" justifyContent="space-between">
                               <Typography component="h1" variant="h1">
                                    {!data.hideTitle ? data.title : null}
                                        <Box className={classes.noprint}>{controlButtons()}</Box>
                                    </Typography>
                                </Box>
                            
                        </div>
                        : <React.Fragment>
                            <Helmet>
                                
                            </Helmet>
                          </React.Fragment>
                    }
                    { overrideHead ?
                        <Helmet>
                            <meta name="description" content={data.description} />
                            <script type="application/ld+json">
                                {JSON.stringify({
                                    "@context": "http://schema.org",
                                    "@type": "Article",
                                    "articleSection": (area.url == "article" || area.url == "showcase") ? area.plural : "",
                                    "name": data.title,
                                    "datePublished": data.modified,
                                    "image": data.thumbnail,
                                    "articleBody": data.content
                                })}
                            </script>
                            <meta property="og:description" content={data.description} />
                            <meta property="og:type" content="article" />
                            <meta property="og:image" content={data.thumbnail} />
                            <meta name="twitter:card" content="summary_large_image" />
                        </Helmet>
                        : null
                    }
                    <div>{renderContent()}</div>
                    {data && !embed ?
                        <>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                {area.features.indexOf("print") !== -1 ? <Tooltip title={"Print page"}>
                                    <IconButton size="small" edge="start" onClick={print}><PrintIcon /></IconButton>
                                </Tooltip> : <div></div> }
                                <small className="rightAlign">Last changed: <Moment format="hh:mma DD/MM/YYYY">{data.modified}</Moment></small>
                            </Box>
                            {area.features.indexOf("socialButtonsx") !== -1 ? <Box className={classes.noprint} align="right">
                                <Typography style={{ display: 'inline-flex'}}>
                                    Share:
                                    <FacebookShareButton url={window.location.origin + baseUrl + data.area + "/" + (data.url || data.id)}>
                                        <Tooltip title={"Share to Facebook"}><FacebookIcon style={{ color: "#3b5998" }} /></Tooltip>
                                    </FacebookShareButton>
                                    <LinkedinShareButton url={window.location.origin + baseUrl + data.area + "/" + (data.url || data.id)} title={AppTitle(data.title)}>
                                        <Tooltip title={"Share to LinkedIn"}><LinkedInIcon style={{ color: "#007fb1" }} /></Tooltip>
                                    </LinkedinShareButton>
                                    <TwitterShareButton url={window.location.origin + baseUrl + data.area + "/" + (data.url || data.id)} title={AppTitle(data.title)}>
                                        <Tooltip title={"Share to Twitter"}><TwitterIcon style={{ color: "#00aced" }} /></Tooltip>
                                    </TwitterShareButton>
                                </Typography>
                            </Box> : null}
                            {console.log(area.features)}
                        </>
                        : null}
                </div>
            ) : (loading ? <CircularProgress /> : (!embed ? (_403 && !auth.user.id ? <SSORequest onSuccess={() => { }} message="Login is required to access this resource" /> : (!auth.loading ? <NotFound /> : <CircularProgress />)) : null))
            }
            <Dialog open={approving} onClose={handleApproveClose} aria-labelledby="form-dialog-title">
                <DialogTitle id="form-dialog-title">Approve Page</DialogTitle>
                <DialogContent>Approve this page? It will become the active version which is displayed to end users.</DialogContent>
                <DialogActions>
                    <Button onClick={handleApproveConfirm} color="primary" startIcon={<ThumbUpIcon />}>Approve</Button>
                    <Button onClick={handleApproveClose} color="default">Cancel</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={restoring} onClose={handleRestoreClose} aria-labelledby="form-dialog-title" fullWidth={true} maxWidth={"lg"}>
                <DialogTitle id="form-dialog-title">Restore Page</DialogTitle>
                <DialogContent>{data?.id ?
                    <Suspense fallback={<CircularProgress />}><VersionPicker id={data.id} onSelect={(selpage) => { setVersion(selpage); }} selected={version ? version.id : 0} /></Suspense>
                    : ""}</DialogContent>
                <DialogActions>
                    {version && version.state == "inactive" ? <Tooltip title={"Set selected as unpublished version. Unpublished version is made inactive."}>
                        <Button onClick={handleRestoreConfirm} color="primary"
                            startIcon={<RestorePageIcon />}>Restore <small>&nbsp;(to workflow)</small></Button></Tooltip> : null}
                    {version && version.state == "active" ? <Tooltip title={"Unpublished version is made inactive (if exists)."}>
                        <Button onClick={handleRestoreConfirm} color="primary"
                            startIcon={<RestorePageIcon />}>Revert unpublished changes</Button></Tooltip> : null}
                    {version && version.state == "inactive" ? <Tooltip title={"Set selected as active version. Unpublished changes are preserved (still unpublished)."}>
                        <Button onClick={handleRevertConfirm} color="primary"
                        startIcon={<RestorePageIcon />}>Revert <small>&nbsp;(to active)</small></Button></Tooltip> : null}
                    <Button onClick={handleRestoreClose} color="default">Cancel</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={deleting} onClose={handleDeleteClose} aria-labelledby="form-dialog-title">
                <DialogTitle id="form-dialog-title">Delete Page</DialogTitle>
                <DialogContent>Move this page to the recycling bin?</DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteConfirm} color="primary" startIcon={<DeleteIcon />}>Delete</Button>
                    <Button onClick={handleDeleteClose} color="default">Cancel</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

const Category = (props) => {
    const { category, items, collection } = props;
    const [collapse, setCollapse] = useState(category.collapseOpen ?? false);
    const contentRef = useRef(null);
    const classes = pageStyles();

    const inner = () => {
        return <Grid container spacing={3}>
            {items.map((item) => {
                var ii = { ...item };
                if (!ii.description) ii.description = ii.pageDescription;
                return (<Grid key={item.id} item sm={6} xs={12}>
                    <PagePreview page={ii} selectedOptions={item.tags} key={item.id} uneditable={true} collection={collection} />
                </Grid>);
            })}
        </Grid>;
    };

    return (<div className={classes.category}>
        <Typography component="h2" variant="h2">{category.name}</Typography>
        <Hijack><div className={classes.description} dangerouslySetInnerHTML={{ __html: category?.description ?? "" }} ref={contentRef}></div></Hijack>
        {category.collapse ?
            <Box>
                <Button className='transcript-accordion' size="small" className={classes.collapse}
                    aria-label={(!collapse ? "Show " : "Hide ") + (category.collapseTitle ?? "items")}
                    onClick={() => setCollapse(!collapse)}
                    disableRipple={true}
                    color="primary"
                    endIcon={<div>{!collapse ? <span className="noprint">+</span> : <span className="noprint">-</span>}<span className="printonly">-</span></div>}>
                    {(!collapse ? "Show " : "Hide ") + (category.collapseTitle ?? "items")}
                </Button>
                <Collapse in={collapse}>
                    {inner()}
                </Collapse>
            </Box>
            :
            inner()}
    </div>);
};

function SSORequest(props) {
    return <>
        {props.message ? <Typography variant="body1">{props.message}</Typography> : null}
        <LoginSSO {...props} />
    </>;
}

export default Page;
